// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/ClinicProfile/sections/OperatingHoursSection.tsx

import React from "react";

type HoursDraftRow = {
  dayOfWeek: number;
  isClosed: boolean;
  openTime: string;
  closeTime: string;
};

export default function OperatingHoursSection(props: {
  editMode: boolean;
  hours: any[];
  draftHours: HoursDraftRow[];
  setDraftHours: React.Dispatch<React.SetStateAction<HoursDraftRow[]>>;
  savingHours: boolean;
  hoursError: string | null;
  hoursSavedAt: string | null;
  onSaveHours: () => void;
  dayName: (n: number) => string;
  compactTime: (t: string | null | undefined) => string;
  formatDateTime: (val: string | null | undefined) => string;
}) {
  const {
    editMode,
    hours,
    draftHours,
    setDraftHours,
    savingHours,
    hoursError,
    hoursSavedAt,
    onSaveHours,
    dayName,
    compactTime,
    formatDateTime,
  } = props;

  const safeHours = Array.isArray(hours) ? hours : [];

  return (
    <details className="ca-clinic-profile__panel" open>
      <summary className="ca-clinic-profile__summary">
        <div>
          <div className="ca-clinic-profile__sectionTitle">Operating hours</div>
          <div className="ca-clinic-profile__hint">Clinic admin-managed</div>
        </div>
      </summary>

      {!editMode ? (
        safeHours.length ? (
          <div className="ca-clinic-profile__row">
            <div className="ca-clinic-profile__label">Weekly schedule</div>
            <div className="ca-clinic-profile__value">
              <div style={{ display: "grid", gap: 6 }}>
                {safeHours.map((h, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", gap: 12, alignItems: "center" }}
                  >
                    <div style={{ minWidth: 90, color: "#6b7a94" }}>
                      {dayName(h.dayOfWeek)}
                    </div>
                    <div>
                      {h.isClosed
                        ? "Closed"
                        : `${compactTime(h.openTime)} – ${compactTime(h.closeTime)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="ca-clinic-profile__row">
            <div className="ca-clinic-profile__label"></div>
            <div className="ca-clinic-profile__value" style={{ color: "#6b7a94" }}>
              —{/* no hours */}
            </div>
          </div>
        )
      ) : (
        <>
          <div className="ca-clinic-profile__row">
            <div className="ca-clinic-profile__label">Weekly schedule</div>
            <div className="ca-clinic-profile__value" style={{ fontWeight: 600 }}>
              <div style={{ display: "grid", gap: 10 }}>
                {draftHours.map((r) => {
                  return (
                    <div
                      key={r.dayOfWeek}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "90px 120px 120px 1fr",
                        gap: 10,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ color: "#6b7a94", fontSize: 13 }}>
                        {dayName(r.dayOfWeek)}
                      </div>

                      <label
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={r.isClosed}
                          disabled={savingHours}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setDraftHours((prev) =>
                              prev.map((x) =>
                                x.dayOfWeek === r.dayOfWeek
                                  ? { ...x, isClosed: checked }
                                  : x
                              )
                            );
                          }}
                        />
                        <span>{r.isClosed ? "Closed" : "Open"}</span>
                      </label>

                      <input
                        type="time"
                        value={r.openTime}
                        disabled={savingHours || r.isClosed}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftHours((prev) =>
                            prev.map((x) =>
                              x.dayOfWeek === r.dayOfWeek ? { ...x, openTime: v } : x
                            )
                          );
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid #d1d9e6",
                        }}
                      />

                      <input
                        type="time"
                        value={r.closeTime}
                        disabled={savingHours || r.isClosed}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftHours((prev) =>
                            prev.map((x) =>
                              x.dayOfWeek === r.dayOfWeek ? { ...x, closeTime: v } : x
                            )
                          );
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid #d1d9e6",
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 8, color: "#6b7a94", fontSize: 13 }}>
                Tip: when “Closed” is checked, times are ignored. Close time must be
                after open time.
              </div>
            </div>
          </div>

          {hoursError ? (
            <div className="ca-clinic-profile__row">
              <div className="ca-clinic-profile__label"></div>
              <div className="ca-clinic-profile__value ca-clinic-profile__row-error">
                {hoursError}
              </div>
            </div>
          ) : null}

          <div className="ca-clinic-profile__row">
            <div className="ca-clinic-profile__label"></div>
            <div className="ca-clinic-profile__value ca-clinic-profile__save-row">
              <button
                className="ca-clinic-profile__btn"
                onClick={onSaveHours}
                disabled={savingHours}
              >
                {savingHours ? "Saving…" : "Save Hours"}
              </button>

              {hoursSavedAt ? (
                <span className="ca-clinic-profile__savedAt">
                  Saved {formatDateTime(hoursSavedAt)}
                </span>
              ) : null}
            </div>
          </div>
        </>
      )}
    </details>
  );
}
